import Decimal from 'decimal.js'
import type { LedgerItem, MoneyDto } from '../api/admin.api'

type FeeBasisEntry = Pick<
  LedgerItem,
  'appliedPercentage' | 'appliedFixedFee' | 'baseAmount' | 'feeMode' | 'feePlanId'
>

type FeeBreakdownEntry = Pick<
  LedgerItem,
  'id' | 'kind' | 'amount' | 'appliedPercentage' | 'appliedFixedFee' | 'baseAmount' | 'feeMode'
>

export type VendepayFeeComponent = {
  key: string
  kind?: string
  type: 'PERCENTAGE' | 'FIXED' | 'TOTAL'
  amount: MoneyDto
  percentage?: string
  baseAmount?: MoneyDto
  inferred?: boolean
}

function positiveMoney(value: MoneyDto | undefined): MoneyDto | null {
  if (!value) return null
  try {
    const amount = new Decimal(value.amount)
    return amount.isFinite() && amount.gt(0) ? value : null
  } catch {
    return null
  }
}

function inferredFixedFee(entry: FeeBreakdownEntry, total: MoneyDto): MoneyDto | null {
  if (!entry.appliedPercentage || !entry.baseAmount) return null
  if (entry.baseAmount.currency !== total.currency) return null

  try {
    const percentageAmount = {
      amount: new Decimal(entry.baseAmount.amount)
        .mul(entry.appliedPercentage)
        .div(100)
        .toFixed(3, Decimal.ROUND_HALF_UP),
      currency: total.currency,
    }
    return positiveMoney({
      amount: new Decimal(total.amount).minus(percentageAmount.amount).toString(),
      currency: total.currency,
    })
  } catch {
    return null
  }
}

export function vendepayFeeComponents(
  entries: readonly FeeBreakdownEntry[],
): VendepayFeeComponent[] {
  const percentageComponents: VendepayFeeComponent[] = []
  const fixedComponents: VendepayFeeComponent[] = []

  for (const entry of entries) {
    const total = entry.amount
    const explicitFixed =
      entry.appliedFixedFee?.currency === total.currency
        ? positiveMoney(entry.appliedFixedFee)
        : null
    const inferredFixed =
      entry.appliedFixedFee !== undefined || entry.feeMode === 'MICRO'
        ? null
        : inferredFixedFee(entry, total)
    const fixed = explicitFixed ?? inferredFixed

    if (!fixed) {
      percentageComponents.push({
        key: `${entry.id}:total`,
        ...(entry.kind ? { kind: entry.kind } : {}),
        type: entry.appliedPercentage && entry.baseAmount ? 'PERCENTAGE' : 'TOTAL',
        amount: total,
        ...(entry.appliedPercentage ? { percentage: entry.appliedPercentage } : {}),
        ...(entry.baseAmount ? { baseAmount: entry.baseAmount } : {}),
      })
      continue
    }

    const percentageAmount = positiveMoney({
      amount: new Decimal(total.amount).minus(fixed.amount).toString(),
      currency: total.currency,
    })
    if (percentageAmount) {
      percentageComponents.push({
        key: `${entry.id}:percentage`,
        ...(entry.kind ? { kind: entry.kind } : {}),
        type: 'PERCENTAGE',
        amount: percentageAmount,
        ...(entry.appliedPercentage ? { percentage: entry.appliedPercentage } : {}),
        ...(entry.baseAmount ? { baseAmount: entry.baseAmount } : {}),
      })
    }
    fixedComponents.push({
      key: `${entry.id}:fixed`,
      ...(entry.kind ? { kind: entry.kind } : {}),
      type: 'FIXED',
      amount: fixed,
      ...(inferredFixed ? { inferred: true } : {}),
    })
  }

  return [...percentageComponents, ...fixedComponents]
}

export function vendepayFeeBasisLabel(entries: readonly FeeBasisEntry[]): string | null {
  const grouped = new Map<
    string,
    {
      percentage: string
      currency: string
      amount: Decimal
      fixed: Decimal
      fixedCurrency: string
      mode?: 'STANDARD' | 'MICRO'
      planId?: string
    }
  >()
  const missingBases = new Set<string>()

  for (const entry of entries) {
    if (!entry.appliedPercentage) continue
    if (!entry.baseAmount) {
      missingBases.add(entry.appliedPercentage)
      continue
    }
    const fixedCurrency = entry.appliedFixedFee?.currency ?? entry.baseAmount.currency
    const key = `${entry.feeMode ?? ''}|${entry.feePlanId ?? ''}|${entry.appliedPercentage}|${entry.baseAmount.currency}|${fixedCurrency}`
    const current = grouped.get(key)
    grouped.set(key, {
      percentage: entry.appliedPercentage,
      currency: entry.baseAmount.currency,
      amount: (current?.amount ?? new Decimal(0)).plus(entry.baseAmount.amount),
      fixed: (current?.fixed ?? new Decimal(0)).plus(entry.appliedFixedFee?.amount ?? 0),
      fixedCurrency,
      ...(entry.feeMode ? { mode: entry.feeMode } : {}),
      ...(entry.feePlanId ? { planId: entry.feePlanId } : {}),
    })
  }

  const labels = [...grouped.values()].map((group) => {
    const mode =
      group.mode === 'MICRO' ? 'Microtransação' : group.mode === 'STANDARD' ? 'Padrão' : null
    const fixed = group.fixed.isZero()
      ? group.mode === 'MICRO'
        ? ' · sem fixa'
        : ''
      : ` + fixa ${group.fixedCurrency} ${group.fixed.toFixed(3)}`
    const plan = group.planId ? ` · plano ${group.planId}` : ''
    return `${mode ? `${mode} · ` : ''}${group.percentage}% sobre ${group.currency} ${group.amount.toFixed(3)}${fixed}${plan}`
  })
  labels.push(...[...missingBases].map((percentage) => `${percentage}% sobre base não informada`))
  return labels.length > 0 ? labels.join(' + ') : null
}
